import clsx from "clsx"
import { Avatar as AvatarPrimitive } from "radix-ui"
import type { ReactNode } from "react"

import { theme, type ThemeKeys } from "../../theme"
import styles from "./Avatar.module.css"

export interface AvatarProps {
  imageUrl?: string
  firstName?: string
  lastName?: string
  size?: 16 | 24 | 28 | 32 | 40
  variant?: "circle" | "square"
  /**
   * The content to show when the imageUrl was not provided
   * or image is not available after delayMs.
   * If not provided, the initials of the first and last name will be shown.
   */
  fallback?: ReactNode
  /**
   * The color of the border around the avatar.
   */
  border?: ThemeKeys<"color">
}

export function Avatar(props: AvatarProps) {
  const {
    imageUrl,
    firstName = "",
    lastName = "",
    size = 32,
    variant = "circle",
    fallback,
    border,
  } = props

  return (
    <AvatarPrimitive.Root
      className={clsx(
        styles.root,
        border && styles.border,
        styles[`size-${size}`],
        styles[`variant-${variant}`],
      )}
      style={{
        borderColor: border && theme.color[border],
      }}
    >
      <AvatarPrimitive.Image src={imageUrl} className={styles.image} />
      <AvatarPrimitive.Fallback
        className={styles.fallback}
        delayMs={imageUrl ? 600 : undefined}
      >
        {fallback ?? getInitials(firstName, lastName)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}

function getInitials(firstName: string, lastName: string) {
  return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
}
